import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  ArrowLeftIcon,
  PencilIcon,
  TrashIcon,
  ChartBarIcon,
  CheckCircleIcon,
  XCircleIcon,
  MinusCircleIcon,
  SparklesIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';
import ExamComparisonChart from '../components/charts/ExamComparisonChart';
import { getAllExamTopicDetails } from '../services/topicService';
import AIAnalysisModal from '../components/AIAnalysisModal';

const ExamDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [exam, setExam] = useState(null);
  const [recentExams, setRecentExams] = useState([]);
  const [topicDetails, setTopicDetails] = useState([]);
  const [loadingTopics, setLoadingTopics] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [expandedSubjects, setExpandedSubjects] = useState({}); // For expandable sub-subjects
  const [expandedTopics, setExpandedTopics] = useState({}); // For expandable topic analysis
  const [showAIModal, setShowAIModal] = useState(false);
  const [aiAnalysisLoading, setAiAnalysisLoading] = useState(false);
  const [studyProgram, setStudyProgram] = useState(null);
  const [loadingProgram, setLoadingProgram] = useState(false);
  const [showDeleteProgramModal, setShowDeleteProgramModal] = useState(false);
  const [deletingProgram, setDeletingProgram] = useState(false);
  const [regeneratingProgram, setRegeneratingProgram] = useState(false);

  useEffect(() => {
    fetchExamDetail();
    fetchRecentExams();
    fetchTopicDetails();
    fetchStudyProgram();
  }, [id]);

  const fetchTopicDetails = async () => {
    try {
      setLoadingTopics(true);
      const response = await getAllExamTopicDetails(id);
      if (response.success) {
        setTopicDetails(response.topicDetails || []);
      }
    } catch (error) {
      console.log('No topic details found or error:', error);
      setTopicDetails([]);
    } finally {
      setLoadingTopics(false);
    }
  };

  const fetchStudyProgram = async () => {
    try {
      setLoadingProgram(true);
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

      // Check if there's a study program for this exam
      const response = await axios.get(
        `${API_URL}/study-programs/active`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success && response.data.data.examId === id) {
        setStudyProgram(response.data.data);
      } else {
        // Also check in history
        const historyResponse = await axios.get(
          `${API_URL}/study-programs/history`,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        if (historyResponse.data.success) {
          const programForThisExam = historyResponse.data.data.find(
            p => p.examId === id
          );
          if (programForThisExam) {
            setStudyProgram(programForThisExam);
          }
        }
      }
    } catch (error) {
      console.log('No study program found for this exam:', error);
      setStudyProgram(null);
    } finally {
      setLoadingProgram(false);
    }
  };

  const fetchExamDetail = async () => {
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_URL}/exams/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      console.log('Exam data:', data); // Debug için
      setExam(data.exam);
    } catch (error) {
      console.error('Error fetching exam:', error);
      alert('Deneme yüklenirken hata oluştu!');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentExams = async () => {
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_URL}/exams?limit=10`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setRecentExams(data.exams || []);
    } catch (error) {
      console.error('Error fetching recent exams:', error);
    }
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      const token = localStorage.getItem('token');
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/exams/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      alert('Deneme başarıyla silindi!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Error deleting exam:', error);
      alert('Silme işlemi başarısız!');
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const handleDeleteProgram = async () => {
    try {
      setDeletingProgram(true);
      const token = localStorage.getItem('token');
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/study-programs/${studyProgram.id}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      alert('Ders programı başarıyla silindi!');
      setStudyProgram(null);
      setShowDeleteProgramModal(false);
    } catch (error) {
      console.error('Error deleting program:', error);
      alert('Program silme işlemi başarısız!');
    } finally {
      setDeletingProgram(false);
    }
  };

  const handleRegenerateProgram = async () => {
    try {
      setRegeneratingProgram(true);
      const token = localStorage.getItem('token');

      // First delete the existing program
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/study-programs/${studyProgram.id}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Then create a new one
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/study-programs/create`,
        { examId: id },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        alert('Ders programı yeniden oluşturuldu!');
        // Refresh the page to show new program
        window.location.reload();
      }
    } catch (error) {
      console.error('Error regenerating program:', error);
      if (error.response?.status === 400) {
        alert(error.response.data.message || 'Program oluşturulurken bir hata oluştu!');
      } else {
        alert('Program yeniden oluşturulurken bir hata oluştu!');
      }
    } finally {
      setRegeneratingProgram(false);
    }
  };

  const handleAIAnalysis = async () => {
    try {
      setAiAnalysisLoading(true);
      const token = localStorage.getItem('token');
      const { data } = await axios.post(
        `${process.env.REACT_APP_API_URL}/exams/${id}/ai-analysis`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (data.success) {
        return data.analysis;
      } else {
        throw new Error(data.message || 'AI analizi başarısız oldu');
      }
    } catch (error) {
      console.error('AI analysis error:', error);
      throw new Error(
        error.response?.data?.message ||
        error.message ||
        'AI analizi yapılırken bir hata oluştu'
      );
    } finally {
      setAiAnalysisLoading(false);
    }
  };

  const toggleSubject = (subjectKey) => {
    setExpandedSubjects(prev => ({
      ...prev,
      [subjectKey]: !prev[subjectKey]
    }));
  };

  const toggleTopicAnalysis = (subject) => {
    setExpandedTopics(prev => ({
      ...prev,
      [subject]: !prev[subject]
    }));
  };

  const getSubjectInfo = (examType) => {
    if (examType === 'TYT') {
      return [
        { key: 'turkish', label: 'Türkçe', total: 40, icon: '📖' },
        { key: 'math', label: 'Matematik', total: 40, icon: '🔢' },
        { key: 'science', label: 'Fen Bilimleri', total: 20, icon: '🔬' },
        { key: 'social', label: 'Sosyal Bilimler', total: 20, icon: '🌍' }
      ];
    }
    return [
      { key: 'mathAyt', label: 'Matematik', total: 40, icon: '🔢' },
      { key: 'physics', label: 'Fizik', total: 14, icon: '⚛️' },
      { key: 'chemistry', label: 'Kimya', total: 13, icon: '🧪' },
      { key: 'biology', label: 'Biyoloji', total: 13, icon: '🧬' }
    ];
  };

  const getNetColor = (net, total) => {
    const percentage = (net / total) * 100;
    if (percentage >= 80) return 'text-green-600';
    if (percentage >= 60) return 'text-blue-600';
    if (percentage >= 40) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getProgressBarColor = (net, total) => {
    const percentage = (net / total) * 100;
    if (percentage >= 80) return 'bg-green-500';
    if (percentage >= 60) return 'bg-blue-500';
    if (percentage >= 40) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!exam) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Deneme Bulunamadı</h2>
          <Link to="/dashboard" className="text-primary-600 hover:underline">
            Dashboard'a Dön
          </Link>
        </div>
      </div>
    );
  }

  const subjects = getSubjectInfo(exam.examType);
  const totalCorrect = Object.values(exam.results).reduce((sum, s) => sum + (s.correct || 0), 0);
  const totalWrong = Object.values(exam.results).reduce((sum, s) => sum + (s.wrong || 0), 0);
  const totalBlank = Object.values(exam.results).reduce((sum, s) => sum + (s.blank || 0), 0);
  const totalNet = Object.values(exam.nets).reduce((sum, net) => sum + (net || 0), 0).toFixed(2);

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <Link
            to="/dashboard"
            className="inline-flex items-center text-primary-600 hover:text-primary-700 mb-4"
          >
            <ArrowLeftIcon className="w-5 h-5 mr-2" />
            Dashboard'a Dön
          </Link>

          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3">
                <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                  exam.examType === 'TYT' 
                    ? 'bg-blue-100 text-blue-800' 
                    : 'bg-purple-100 text-purple-800'
                }`}>
                  {exam.examType}
                </span>
                <h1 className="text-3xl font-bold text-gray-900">
                  {new Date(exam.examDate).toLocaleDateString('tr-TR', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </h1>
              </div>
              <p className="text-gray-600 mt-1">
                {new Date(exam.createdAt).toLocaleDateString('tr-TR')} tarihinde eklendi
              </p>
            </div>

            <div className="flex gap-3">
              {studyProgram && (
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      if (studyProgram.status === 'active') {
                        navigate('/study-program');
                      } else {
                        navigate(`/study-program/${studyProgram.id}`);
                      }
                    }}
                    className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-lg hover:from-emerald-700 hover:to-teal-700 transition shadow-md hover:shadow-lg"
                  >
                    <CalendarIcon className="w-5 h-5 mr-2" />
                    {studyProgram.status === 'active' ? 'Aktif Programım' : 'Programı Görüntüle'}
                  </button>
                  <button
                    onClick={handleRegenerateProgram}
                    disabled={regeneratingProgram}
                    className="inline-flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Programı yeniden oluştur"
                  >
                    {regeneratingProgram ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    )}
                  </button>
                  <button
                    onClick={() => setShowDeleteProgramModal(true)}
                    className="inline-flex items-center px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition shadow-md hover:shadow-lg"
                    title="Programı sil"
                  >
                    <TrashIcon className="w-5 h-5" />
                  </button>
                </div>
              )}
              <button
                onClick={() => navigate(`/exam/${exam.id}/ai-analysis`)}
                className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:from-purple-700 hover:to-indigo-700 transition shadow-md hover:shadow-lg"
              >
                <SparklesIcon className="w-5 h-5 mr-2" />
                AI Analizi
              </button>
              <button
                onClick={() => navigate(`/edit-exam/${exam.id}`)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition"
              >
                <PencilIcon className="w-5 h-5 mr-2" />
                Düzenle
              </button>
              <button
                onClick={() => setShowDeleteModal(true)}
                className="inline-flex items-center px-4 py-2 border border-red-300 rounded-lg text-red-700 bg-white hover:bg-red-50 transition"
              >
                <TrashIcon className="w-5 h-5 mr-2" />
                Sil
              </button>
            </div>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm font-medium">Toplam Net</p>
                <p className="text-3xl font-bold mt-1">{totalNet}</p>
              </div>
              <ChartBarIcon className="w-12 h-12 text-blue-200" />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Doğru</p>
                <p className="text-2xl font-bold text-green-600 mt-1">{totalCorrect}</p>
              </div>
              <CheckCircleIcon className="w-10 h-10 text-green-500" />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Yanlış</p>
                <p className="text-2xl font-bold text-red-600 mt-1">{totalWrong}</p>
              </div>
              <XCircleIcon className="w-10 h-10 text-red-500" />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Boş</p>
                <p className="text-2xl font-bold text-gray-600 mt-1">{totalBlank}</p>
              </div>
              <MinusCircleIcon className="w-10 h-10 text-gray-400" />
            </div>
          </div>
        </div>

        {/* Subject Details Table */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden mb-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900">Ders Bazlı Detaylar</h2>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ders
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Doğru
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Yanlış
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Boş
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Net
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Başarı
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {subjects.map((subject) => {
                  const result = exam.results[subject.key] || { correct: 0, wrong: 0, blank: 0 };
                  const net = exam.nets[subject.key] || 0;
                  const percentage = (net / subject.total) * 100;

                  // Alt dallar için özel render
                  const subSubjects = exam.examType === 'TYT' && subject.key === 'science' 
                    ? [
                        { key: 'physics', label: 'Fizik', icon: '⚛️', total: 7 },
                        { key: 'chemistry', label: 'Kimya', icon: '🧪', total: 7 },
                        { key: 'biology', label: 'Biyoloji', icon: '🧬', total: 6 }
                      ]
                    : exam.examType === 'TYT' && subject.key === 'social'
                    ? [
                        { key: 'history', label: 'Tarih', icon: '🏛️', total: 5 },
                        { key: 'geography', label: 'Coğrafya', icon: '🗺️', total: 5 },
                        { key: 'philosophy', label: 'Felsefe', icon: '💭', total: 5 },
                        { key: 'religion', label: 'Din Kültürü', icon: '☪️', total: 5 }
                      ]
                    : null;

                  return (
                    <React.Fragment key={subject.key}>
                      <tr className="hover:bg-gray-50 transition">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            {subSubjects && (
                              <button
                                onClick={() => toggleSubject(subject.key)}
                                className="text-gray-400 hover:text-gray-600 transition"
                              >
                                <svg
                                  className={`w-5 h-5 transition-transform ${expandedSubjects[subject.key] ? 'rotate-90' : ''}`}
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                              </button>
                            )}
                            <span className="text-2xl">{subject.icon}</span>
                            <div>
                              <div className="text-sm font-medium text-gray-900 flex items-center gap-2">
                                {subject.label}
                                {subSubjects && (
                                  <span className="text-xs text-gray-400">
                                    ({subSubjects.length} alt dal)
                                  </span>
                                )}
                              </div>
                              <div className="text-sm text-gray-500">
                                {subject.total} Soru
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            {result.correct}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            {result.wrong}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            {result.blank}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className={`text-lg font-bold ${getNetColor(net, subject.total)}`}>
                            {net.toFixed(2)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="w-full">
                            <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                              <span>{percentage.toFixed(0)}%</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div
                                className={`h-2 rounded-full transition-all ${getProgressBarColor(net, subject.total)}`}
                                style={{ width: `${Math.min(percentage, 100)}%` }}
                              />
                            </div>
                          </div>
                        </td>
                      </tr>
                      {/* Alt dallar - Sadece expand edildiğinde göster */}
                      {subSubjects && expandedSubjects[subject.key] && subSubjects.map((subSubject) => {
                        const subResult = exam.results[subSubject.key] || { correct: 0, wrong: 0, blank: 0 };
                        const subNet = exam.nets[subSubject.key] || 0;
                        const subPercentage = (subNet / subSubject.total) * 100;
                        
                        return (
                          <tr key={`${subject.key}-${subSubject.key}`} className="bg-gray-50/50 hover:bg-gray-100 transition">
                            <td className="px-6 py-3 whitespace-nowrap pl-16">
                              <div className="flex items-center">
                                <span className="text-lg mr-2">{subSubject.icon}</span>
                                <div>
                                  <div className="text-xs font-medium text-gray-700">
                                    {subSubject.label}
                                  </div>
                                  <div className="text-xs text-gray-400">
                                    {subSubject.total} Soru
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-3 whitespace-nowrap text-center">
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700">
                                {subResult.correct}
                              </span>
                            </td>
                            <td className="px-6 py-3 whitespace-nowrap text-center">
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-700">
                                {subResult.wrong}
                              </span>
                            </td>
                            <td className="px-6 py-3 whitespace-nowrap text-center">
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-50 text-gray-600">
                                {subResult.blank}
                              </span>
                            </td>
                            <td className="px-6 py-3 whitespace-nowrap text-center">
                              <span className={`text-sm font-bold ${getNetColor(subNet, subSubject.total)}`}>
                                {subNet.toFixed(2)}
                              </span>
                            </td>
                            <td className="px-6 py-3 whitespace-nowrap">
                              <div className="w-full">
                                <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                                  <span>{subPercentage.toFixed(0)}%</span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-1.5">
                                  <div
                                    className={`h-1.5 rounded-full transition-all ${getProgressBarColor(subNet, subSubject.total)}`}
                                    style={{ width: `${Math.min(subPercentage, 100)}%` }}
                                  />
                                </div>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Comparison Chart */}
        {recentExams.length > 1 && (
          <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">📊 Ortalama ile Karşılaştırma</h2>
            <ExamComparisonChart currentExam={exam} recentExams={recentExams} />
          </div>
        )}

        {/* Performance Insights */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">💡 Performans Analizi</h2>
          <div className="space-y-3">
            {subjects.map((subject) => {
              const net = exam.nets[subject.key] || 0;
              const percentage = (net / subject.total) * 100;
              
              if (percentage >= 80) {
                return (
                  <div key={subject.key} className="flex items-start p-3 bg-green-50 rounded-lg">
                    <span className="text-green-500 text-xl mr-3">✅</span>
                    <div>
                      <p className="text-sm font-medium text-green-900">
                        {subject.label}'de çok iyi!
                      </p>
                      <p className="text-xs text-green-700">
                        {net.toFixed(2)} net ile %{percentage.toFixed(0)} başarı oranı
                      </p>
                    </div>
                  </div>
                );
              }
              
              if (percentage < 60) {
                return (
                  <div key={subject.key} className="flex items-start p-3 bg-yellow-50 rounded-lg">
                    <span className="text-yellow-500 text-xl mr-3">⚠️</span>
                    <div>
                      <p className="text-sm font-medium text-yellow-900">
                        {subject.label}'e daha fazla odaklan
                      </p>
                      <p className="text-xs text-yellow-700">
                        {net.toFixed(2)} net - Hedef: {(subject.total * 0.7).toFixed(0)} net
                      </p>
                    </div>
                  </div>
                );
              }
              
              return null;
            }).filter(Boolean)}
          </div>
        </div>

        {/* Topic Analysis Section */}
        {topicDetails && topicDetails.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">🎯 Konu Bazlı Analiz</h2>
            
            {topicDetails.map((detail) => (
              <div key={detail.subject} className="mb-6 last:mb-0 border border-gray-200 rounded-lg">
                <button
                  onClick={() => toggleTopicAnalysis(detail.subject)}
                  className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <svg
                      className={`w-5 h-5 text-gray-400 transition-transform ${expandedTopics[detail.subject] ? 'rotate-90' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    <span className="bg-primary-100 text-primary-700 px-3 py-1 rounded-lg font-semibold">
                      {detail.subject}
                    </span>
                    <span className="text-sm text-gray-500">
                      ({detail.topicResults.length} konu)
                    </span>
                  </div>
                  <span className="text-xs text-gray-400">
                    {expandedTopics[detail.subject] ? 'Gizle' : 'Göster'}
                  </span>
                </button>

                {expandedTopics[detail.subject] && (
                  <div className="p-4">
                    <div className="grid gap-3">
                  {detail.topicResults && detail.topicResults.map((topic) => {
                    const net = Math.max(0, topic.correct - (topic.wrong / 4));
                    const successRate = topic.total > 0 ? (net / topic.total) * 100 : 0;
                    
                    return (
                      <div
                        key={topic.topicId}
                        className="border border-gray-200 rounded-lg p-3 hover:border-primary-300 transition"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-gray-900">{topic.topicName}</span>
                          <span className={`text-lg font-bold ${
                            successRate >= 80 ? 'text-green-600' :
                            successRate >= 60 ? 'text-yellow-600' :
                            'text-red-600'
                          }`}>
                            {net.toFixed(2)}
                          </span>
                        </div>

                        <div className="flex items-center gap-4 text-xs text-gray-600 mb-2">
                          <span className="flex items-center">
                            <span className="w-2 h-2 bg-green-500 rounded-full mr-1"></span>
                            D: {topic.correct}
                          </span>
                          <span className="flex items-center">
                            <span className="w-2 h-2 bg-red-500 rounded-full mr-1"></span>
                            Y: {topic.wrong}
                          </span>
                          <span className="flex items-center">
                            <span className="w-2 h-2 bg-gray-400 rounded-full mr-1"></span>
                            B: {topic.empty}
                          </span>
                          <span className="text-gray-400">/ {topic.total} soru</span>
                        </div>

                        {/* Progress bar */}
                        <div className="w-full bg-gray-200 rounded-full h-1.5">
                          <div
                            className={`h-1.5 rounded-full transition-all ${
                              successRate >= 80 ? 'bg-green-500' :
                              successRate >= 60 ? 'bg-yellow-500' :
                              'bg-red-500'
                            }`}
                            style={{ width: `${Math.min(successRate, 100)}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                    </div>

                    {/* Weak Topics Alert */}
                    {(() => {
                      const weakTopics = detail.topicResults.filter(topic => {
                        const net = Math.max(0, topic.correct - (topic.wrong / 4));
                        return net < (topic.total * 0.5);
                      });
                      
                      if (weakTopics.length === 0) return null;
                      
                      return (
                        <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                          <p className="text-sm font-medium text-yellow-900 mb-2">
                            ⚠️ Çalışman Gereken Konular:
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {weakTopics.map((topic) => (
                              <span
                                key={topic.topicId}
                                className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded"
                              >
                                {topic.topicName}
                              </span>
                            ))}
                          </div>
                        </div>
                      );
                    })()}

                    {/* Strong Topics */}
                    {(() => {
                      const strongTopics = detail.topicResults.filter(topic => {
                        const net = Math.max(0, topic.correct - (topic.wrong / 4));
                        return net >= (topic.total * 0.8);
                      });
                      
                      if (strongTopics.length === 0) return null;
                      
                      return (
                        <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                          <p className="text-sm font-medium text-green-900 mb-2">
                            ✅ Güçlü Olduğun Konular:
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {strongTopics.map((topic) => (
                              <span
                                key={topic.topicId}
                                className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded"
                              >
                                {topic.topicName}
                              </span>
                            ))}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Hedef Karşılaştırması */}
      {user?.targetNets && (
        <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl shadow-sm p-6 border border-purple-200 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">🎯 Hedef Karşılaştırması</h2>
            <Link
              to="/goals"
              className="text-sm text-purple-600 hover:text-purple-700 font-medium"
            >
              Hedefleri Düzenle →
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {subjects.map((subject) => {
              const currentNet = exam.nets[subject.key] || 0;
              const targetNet = exam.examType === 'TYT' 
                ? user.targetNets.tyt[subject.key] 
                : user.targetNets.ayt[subject.key];

              if (!targetNet || targetNet === 0) return null;

              const percentage = (currentNet / targetNet) * 100;
              const difference = currentNet - targetNet;

              return (
                <div key={subject.key} className="bg-white rounded-lg p-4 border border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-2xl">{subject.icon}</span>
                    <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                      percentage >= 100 ? 'bg-green-100 text-green-700' :
                      percentage >= 80 ? 'bg-yellow-100 text-yellow-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      %{percentage.toFixed(0)}
                    </span>
                  </div>
                  
                  <h4 className="font-semibold text-gray-900 mb-2">{subject.label}</h4>
                  
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Mevcut:</span>
                      <span className="font-bold text-gray-900">{currentNet.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Hedef:</span>
                      <span className="font-bold text-purple-600">{targetNet.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Fark:</span>
                      <span className={`font-bold ${
                        difference >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {difference >= 0 ? '+' : ''}{difference.toFixed(2)}
                      </span>
                    </div>
                  </div>

                  <div className="mt-3">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all ${
                          percentage >= 100 ? 'bg-green-500' :
                          percentage >= 80 ? 'bg-yellow-500' :
                          'bg-red-500'
                        }`}
                        style={{ width: `${Math.min(percentage, 100)}%` }}
                      />
                    </div>
                  </div>

                  {percentage < 100 && (
                    <p className="text-xs text-gray-600 mt-2">
                      Hedefe {(targetNet - currentNet).toFixed(2)} net kaldı
                    </p>
                  )}
                  {percentage >= 100 && (
                    <p className="text-xs text-green-600 mt-2 font-semibold">
                      ✅ Hedefe ulaştın!
                    </p>
                  )}
                </div>
              );
            }).filter(Boolean)}
          </div>

          {subjects.every(s => {
            const targetNet = exam.examType === 'TYT' 
              ? user.targetNets.tyt[s.key] 
              : user.targetNets.ayt[s.key];
            return !targetNet || targetNet === 0;
          }) && (
            <div className="text-center py-8">
              <p className="text-gray-600 mb-4">Henüz hedef net belirlemediniz</p>
              <Link
                to="/goals"
                className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition"
              >
                Hedef Belirle →
              </Link>
            </div>
          )}
        </div>
      )}

      {/* AI Analysis Modal */}
      <AIAnalysisModal
        isOpen={showAIModal}
        onClose={() => setShowAIModal(false)}
        exam={exam}
        onAnalyze={handleAIAnalysis}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4">
              <TrashIcon className="w-6 h-6 text-red-600" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 text-center mb-2">
              Denemeyi Sil
            </h3>
            <p className="text-gray-600 text-center mb-6">
              Bu denemeyi silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                disabled={deleting}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
              >
                İptal
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50"
              >
                {deleting ? 'Siliniyor...' : 'Sil'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Program Confirmation Modal */}
      {showDeleteProgramModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4">
              <TrashIcon className="w-6 h-6 text-red-600" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 text-center mb-2">
              Ders Programını Sil
            </h3>
            <p className="text-gray-600 text-center mb-6">
              Bu ders programını silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
              Tüm görevler ve ilerleme kaydedilmez.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteProgramModal(false)}
                disabled={deletingProgram}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
              >
                İptal
              </button>
              <button
                onClick={handleDeleteProgram}
                disabled={deletingProgram}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50"
              >
                {deletingProgram ? 'Siliniyor...' : 'Sil'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExamDetail;
